import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import AppTile from "@/components/AppTile";
import AppDialog from "@/components/AppDialog";
import CategorySidebar, { ALL, FAVORITES, UNCATEGORIZED } from "@/components/CategorySidebar";
import CommandPalette from "@/components/CommandPalette";
import { useAuth } from "@/contexts/AuthContext";
import { useWS } from "@/contexts/WebSocketContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DashboardPage() {
    const { user } = useAuth();
    const { statuses } = useWS();
    const isAdmin = user && user !== false && user.role === "admin";

    const [apps, setApps] = useState([]);
    const [categories, setCategories] = useState([]);
    const [history, setHistory] = useState({});
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [editApp, setEditApp] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    // Selected category lives in the URL so a filtered view can be bookmarked.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCategory = searchParams.get("cat") || ALL;
    const selectCategory = (key) => {
        const next = new URLSearchParams(searchParams);
        if (key === ALL) next.delete("cat");
        else next.set("cat", key);
        setSearchParams(next, { replace: true });
    };

    const fetchApps = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/apps");
            setApps(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load apps");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const { data } = await api.get("/categories");
            setCategories(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load categories");
        }
    };

    const fetchHistory = async () => {
        try {
            const { data } = await api.get("/apps/history", { params: { hours: 24, buckets: 24 } });
            setHistory(data || {});
        } catch (_e) {
            // Sparklines are decorative; a failure here shouldn't nag the user.
        }
    };

    useEffect(() => {
        fetchApps();
        fetchCategories();
        fetchHistory();
        // History only shifts by a bucket every hour, so poll gently.
        const t = setInterval(fetchHistory, 5 * 60 * 1000);
        return () => clearInterval(t);
    }, []);

    const handleDelete = async () => {
        if (!pendingDelete) return;
        try {
            await api.delete(`/apps/${pendingDelete.id}`);
            toast.success("App deleted");
            setPendingDelete(null);
            fetchApps();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const toggleFavorite = async (a) => {
        try {
            await api.put(`/apps/${a.id}`, { favorite: !a.favorite });
            fetchApps();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const categoryById = useMemo(
        () => Object.fromEntries(categories.map((c) => [c.id, c])),
        [categories],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = apps.slice();
        if (q) {
            list = list.filter((a) => {
                const catName = categoryById[a.category_id]?.name?.toLowerCase();
                return (
                    a.name?.toLowerCase().includes(q) ||
                    a.host?.toLowerCase().includes(q) ||
                    a.description?.toLowerCase().includes(q) ||
                    catName?.includes(q) ||
                    (a.tags || []).some((t) => t.toLowerCase().includes(q))
                );
            });
        }
        if (selectedCategory === FAVORITES) {
            list = list.filter((a) => a.favorite);
        } else if (selectedCategory === UNCATEGORIZED) {
            list = list.filter((a) => !a.category_id);
        } else if (selectedCategory !== ALL) {
            list = list.filter((a) => a.category_id === selectedCategory);
        }
        list.sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
        return list;
    }, [apps, query, selectedCategory, categoryById]);

    const headingFor = () => {
        if (selectedCategory === ALL) return "My Apps";
        if (selectedCategory === FAVORITES) return "Favorites";
        if (selectedCategory === UNCATEGORIZED) return "Uncategorized";
        const c = categoryById[selectedCategory];
        return c ? `${c.icon} ${c.name}` : "My Apps";
    };

    return (
        <>
            <Header />
            <CommandPalette
                apps={apps}
                categories={categories}
                onSelectCategory={selectCategory}
            />
            <main className="max-w-[1600px] mx-auto px-6 sm:px-10 py-10 sm:py-12" data-testid="dashboard">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">
                            {headingFor()}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold">
                            Hello, {user?.username}
                        </h1>
                        <p className="text-muted-foreground mt-2 max-w-xl">
                            Your services at a glance — click any tile to launch.
                        </p>
                    </div>
                    {isAdmin && (
                        <Button
                            onClick={() => {
                                setEditApp(null);
                                setDialogOpen(true);
                            }}
                            className="rounded-full h-11 px-5"
                            data-testid="add-app-button"
                        >
                            <Plus className="h-4 w-4 mr-1.5" /> Add app
                        </Button>
                    )}
                </div>

                <div className="flex gap-8">
                    <CategorySidebar
                        categories={categories}
                        apps={apps}
                        selected={selectedCategory}
                        onSelect={selectCategory}
                    />

                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-3 mb-8">
                            <div className="relative w-full max-w-md">
                                <Search className="h-4 w-4 absolute top-3 left-3 text-muted-foreground" />
                                <Input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search apps, hosts, categories, tags..."
                                    className="pl-9 pr-16 h-11 rounded-full"
                                    data-testid="search-apps"
                                />
                                <kbd className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-3 h-6 items-center gap-0.5 rounded border border-border bg-muted/60 px-1.5 text-[10px] font-medium text-muted-foreground pointer-events-none">
                                    ⌘K
                                </kbd>
                            </div>
                            <CategorySidebar
                                categories={categories}
                                apps={apps}
                                selected={selectedCategory}
                                onSelect={selectCategory}
                                variant="chips"
                            />
                        </div>

                        {loading ? (
                            <div className="text-muted-foreground">Loading apps...</div>
                        ) : filtered.length === 0 ? (
                            <div className="glass-tile rounded-3xl p-12 text-center">
                                <h3 className="font-display text-2xl font-semibold mb-2">
                                    {apps.length === 0 ? "No apps yet" : "Nothing in this category"}
                                </h3>
                                <p className="text-muted-foreground mb-6">
                                    {apps.length > 0
                                        ? "Try another category, or clear your search."
                                        : isAdmin
                                          ? "Add your first app to start monitoring."
                                          : "An admin needs to grant you access to apps."}
                                </p>
                                {isAdmin && apps.length === 0 && (
                                    <Button
                                        onClick={() => {
                                            setEditApp(null);
                                            setDialogOpen(true);
                                        }}
                                        data-testid="empty-add-app"
                                    >
                                        <Plus className="h-4 w-4 mr-1.5" /> Add your first app
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                                {filtered.map((a) => (
                                    <AppTile
                                        key={a.id}
                                        app={a}
                                        status={statuses[a.id]}
                                        isAdmin={isAdmin}
                                        category={categoryById[a.category_id]}
                                        history={history[a.id]}
                                        onEdit={(app) => {
                                            setEditApp(app);
                                            setDialogOpen(true);
                                        }}
                                        onDelete={(app) => setPendingDelete(app)}
                                        onToggleFavorite={toggleFavorite}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {isAdmin && (
                <AppDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    initial={editApp}
                    categories={categories}
                    defaultCategoryId={
                        [ALL, FAVORITES, UNCATEGORIZED].includes(selectedCategory)
                            ? ""
                            : selectedCategory
                    }
                    onSaved={fetchApps}
                />
            )}

            <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this app?</AlertDialogTitle>
                        <AlertDialogDescription>
                            "{pendingDelete?.name}" will be removed from the dashboard. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete-app">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-app"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
